import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Accordion, Alert, Anchor, Badge, Button, Card, Group, Loader, Modal, Pagination, Stack, Text, Timeline } from "@mantine/core";
import { IconBell } from "@tabler/icons-react";

import { useEnrollmentDetails, useEnrollmentNotifications, useEnrollmentPayments } from "@hooks/queries/masterclass/useEnrollmentActivity";


export type EnrollmentAction = "details" | "payments" | "notifications";

export const EnrollmentActivityModal = ({ id, action, close }: {
  id: string; action: EnrollmentAction; close: () => void;
}) => {
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(1);
  const details = useEnrollmentDetails(id, action === "details");
  const payments = useEnrollmentPayments(id, page, action === "payments");
  const notifications = useEnrollmentNotifications(id, page, action === "notifications");
  const active = action === "details" ? details : action === "payments" ? payments : notifications;
  const pagination = action === "payments" ? payments.data?.pagination : notifications.data?.pagination;
  const date = (value?: string) => value ? new Date(value).toLocaleString(i18n.language) : "—";
  const color = (status?: string) => ["paid", "delivered", "read", "processed"].includes(status || "")
    ? "green" : status === "failed" ? "red" : "gray";
  const field = (label: string, value: unknown) => (
    <Group justify="space-between" align="start" wrap="nowrap" key={label}>
      <Text size="sm" c="dimmed">{t(label)}</Text>
      <Text size="sm" ta="right" style={{ overflowWrap: "anywhere" }}>{value == null || value === "" ? "—" : String(value)}</Text>
    </Group>
  );

  return (
    <Modal opened onClose={close} centered size="lg" title={t(action === "details" ? "view-enrollment" : action === "payments" ? "payment-activity" : "notification-history")}>
      <Stack gap="md">
        {active.isLoading && active.fetchStatus !== "idle" && <Loader size="sm" />}
        {active.isError && <Alert color="red">{t("enrollment-activity-error")}<Button variant="subtle" onClick={() => active.refetch()}>{t("try-again")}</Button></Alert>}
        {action === "details" && details.data && (() => {
          const { enrollment: e, masterclass: m, payment: p } = details.data;
          return <>
            <Text fw={600}>{e.first_name} {e.surname}</Text>
            {field("masterclass", m.name)}
            {field("provider", m.provider_name)}
            {field("date", date(m.datetime))}
            {field("mode", t(m.mode.replace("_", "-")))}
            {field("location", m.location)}
            {field("email", e.email)}
            {field("phone-number", e.phone_number)}
            {field("whatsapp-phone-number", e.whatsapp_phone_number)}
            {field("dob", e.dob ? new Date(e.dob).toLocaleDateString(i18n.language) : undefined)}
            {field("gender", t(e.gender))}
            {field("residence-location", e.residence_location)}
            {field("industry", e.industry)}
            {field("participation-reason", e.participation_reason)}
            {field("enrollment-date", date(e.created_at))}
            {field("last-updated", date(e.updated_at))}
            {field("payment-status", t(`enrollment-payment-${p.status}`))}
            {field("enrollment-amount-due", `${p.amount_due} ${p.currency}`)}
            {p.discount_percent > 0 && field("discount", `${p.discount_percent}%`)}
            {!!details.data.answers.length && <Text fw={600} size="sm">{t("enrollment-additional-answers")}</Text>}
            {details.data.answers.map(answer => field(answer.label, answer.value))}
            {e.google_calendar_link && <Anchor href={e.google_calendar_link} target="_blank" rel="noopener noreferrer">{t("enrollment-open-calendar")}</Anchor>}
          </>;
        })()}
        {action === "payments" && payments.data && <>
          {payments.data.transaction && <Card withBorder>
            <Stack gap="xs">
              <Text fw={600}>{t("enrollment-recorded-payment")}</Text>
              {field("payment-activity-amount", `${payments.data.transaction.amount_paid ?? "—"} ${payments.data.transaction.currency || "XOF"}`)}
              {field("payment-exception-reference", payments.data.transaction.reference)}
              {field("provider", payments.data.transaction.received_by)}
              {field("date", date(payments.data.transaction.paid_at || payments.data.transaction.date))}
            </Stack>
          </Card>}
          {payments.data.attempts.map(attempt => <Card key={attempt.id} withBorder>
            <Stack gap="xs">
              <Group justify="space-between"><Text fw={600}>{attempt.provider || "—"}</Text><Badge color={color(attempt.status)}>{attempt.processed_at ? t("payment-activity-applied") : attempt.settlement_review_reason ? t("payment-activity-review") : t(attempt.status || "enrollment-status-unknown")}</Badge></Group>
              {field("payment-activity-amount", `${attempt.amount ?? "—"} ${attempt.currency || ""}`)}
              {field("payment-exception-reference", attempt.reference)}
              {field("date", date(attempt.created_at))}
              {attempt.last_failed_charge_reason && field("payment-activity-last-failure", attempt.last_failed_charge_reason)}
              {attempt.settlement_review_reason && field("payment-exception-reason", attempt.settlement_review_reason)}
            </Stack>
          </Card>)}
          {!payments.data.attempts.length && <Text c="dimmed" size="sm">{t("payment-activity-none")}</Text>}
          <Text c="dimmed" size="xs">{t("payment-activity-history-note")}</Text>
        </>}
        {action === "notifications" && notifications.data && <>
          {!notifications.data.notifications.length && <Text c="dimmed">{t("no-notifications-found")}</Text>}
          <Timeline bulletSize={24} lineWidth={2}>
            {notifications.data.notifications.map(notification => <Timeline.Item key={notification._id} bullet={<IconBell size={12} />} title={t(notification.notification_type)}>
              <Text size="xs" c="dimmed">{date(notification.created_at)}</Text>
              <Accordion variant="separated" mt="sm">
                {(notification.recipients || []).map((recipient, index) => <Accordion.Item key={`${recipient.recipient_id}-${index}`} value={String(index)}>
                  <Accordion.Control><Group justify="space-between"><Text size="sm">{recipient.recipient_id} · {recipient.channels.join(", ")}</Text><Badge color={color(recipient.status)}>{t(recipient.status || "enrollment-status-unknown")}</Badge></Group></Accordion.Control>
                  <Accordion.Panel><Stack gap="xs">
                    {!recipient.delivery_attempts?.length && <Text size="sm" c="dimmed">{t("enrollment-no-delivery-evidence")}</Text>}
                    {recipient.delivery_attempts?.map((attempt, attemptIndex) => <Stack key={attemptIndex} gap={3}>
                      <Group justify="space-between"><Text size="sm">{attempt.channel}</Text><Badge color={color(attempt.status)}>{t(attempt.status)}</Badge></Group>
                      <Text size="xs" c="dimmed">{date(attempt.attempt_time)}</Text>
                      {attempt.error_message && <Text size="xs" c="red">{attempt.error_message}</Text>}
                    </Stack>)}
                  </Stack></Accordion.Panel>
                </Accordion.Item>)}
              </Accordion>
            </Timeline.Item>)}
          </Timeline>
        </>}
        {pagination && pagination.total > pagination.page_size && <Pagination value={page} onChange={setPage} total={Math.ceil(pagination.total / pagination.page_size)} disabled={active.isFetching} />}
      </Stack>
    </Modal>
  );
};
